import * as React from "react";
import { History, Maximize2, Minimize2, Plus, X } from "lucide-react";
import { useToggle } from "react-use";
import { toast } from "react-toastify";
import { ulid } from "ulid";
import { Button } from "@/components/ui/button";
import { ChatInput } from "@/components/chat/ChatInput";
import { PathfinderTriangleIcon } from "@/components/ui/PathfinderTriangleIcon";
import {
  useSnapshotConversationAskMutation,
  useCancelSnapshotConversationMutation,
  useSnapshotConversationMessagesQuery,
} from "@/modules/snapshots/snapshotConversation.api";
import type { Citation, ListResult, QaScopeStrategy, SnapshotConversation, SnapshotConversationMessage } from "./types";
import { SnapshotQaWebsocket } from "./SnapshotQaWebsocket";
import { SnapshotQaMessages } from "./SnapshotQaMessages";
import { SnapshotQaHistory } from "./SnapshotQaHistory";
import { keepLatestTurn, mergeConversationMessages } from "./mergeConversationMessages";
import { useQaProgress } from "./useQaProgress";

interface Props {
  businessReportId: string;
  snapshotCollectionId: string;
  subcollectionId?: string;
  activeFilters: { snapshot_ids: string[] } | null;
  onCitationClick: (citation: Citation) => void;
  onOutcomeClick: (outcomeId: string, detectedFields: string[]) => void;
  onOpenQa: (outcomeId: string) => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onClose?: () => void;
  contractFamilyProjection?: boolean;
}

export const SnapshotQaPanel: React.FC<Props> = ({
  businessReportId,
  snapshotCollectionId,
  subcollectionId,
  activeFilters,
  onCitationClick,
  onOutcomeClick,
  onOpenQa,
  isExpanded,
  onToggleExpand,
  onClose,
  contractFamilyProjection = false,
}) => {
  const [showHistory, toggleShowHistory] = useToggle(false);
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  const [queryId, setQueryId] = React.useState<string | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [streamingContent, setStreamingContent] = React.useState("");
  const streamingContentRef = React.useRef("");
  const [localMessages, setLocalMessages] = React.useState<SnapshotConversationMessage[]>([]);
  const [lastSentMessageId, setLastSentMessageId] = React.useState<string | null>(null);
  const [savedMessagesAtSend, setSavedMessagesAtSend] = React.useState<SnapshotConversationMessage[] | undefined>();
  const routingModeRef = React.useRef<string>("PROSE");
  const detectedFieldsRef = React.useRef<string[]>([]);
  const [source, setSource] = React.useState<string | null>(null);
  const sourceRef = React.useRef<string | null>(null);
  const streamingListResultsRef = React.useRef<ListResult[]>([]);
  const [streamingListResults, setStreamingListResults] = React.useState<ListResult[]>([]);
  const {
    metrics: progressMetrics,
    phase: progressPhase,
    setPhase: setProgressPhase,
    setRoutingMode: setProgressRoutingMode,
    setDetectedFields: setProgressDetectedFields,
    setDocumentCount: setProgressDocumentCount,
    addAnalyzedCount: addProgressAnalyzedCount,
    setMapReduce: setProgressMapReduce,
    setBatchProgress: setProgressBatchProgress,
    setScope: setProgressScope,
    setSelectedDocuments: setProgressSelectedDocuments,
    reset: resetProgress,
  } = useQaProgress();

  const [askConversation] = useSnapshotConversationAskMutation();
  const [cancelConversation] = useCancelSnapshotConversationMutation();

  // currentData, not data: data keeps the previous conversation's messages while a new one loads.
  const { currentData: savedMessagesData } = useSnapshotConversationMessagesQuery(
    { conversation_id: conversationId! },
    { skip: !conversationId },
  );

  const allMessages = React.useMemo(
    () =>
      mergeConversationMessages(conversationId ? (savedMessagesData ?? []) : [], localMessages, {
        sentMessageId: lastSentMessageId,
        savedCaughtUp: savedMessagesData !== savedMessagesAtSend,
      }),
    [conversationId, savedMessagesData, savedMessagesAtSend, localMessages, lastSentMessageId],
  );

  const handleSubmit = React.useCallback(
    async (query: string) => {
      const userMessage: SnapshotConversationMessage = {
        id: ulid(),
        role: "human",
        content: query,
        mode: null,
        citations: null,
        matched_documents: null,
        source: null,
        created_at: new Date().toISOString(),
      };

      setLocalMessages(prev => [...keepLatestTurn(prev, lastSentMessageId), userMessage]);
      setLastSentMessageId(userMessage.id);
      setSavedMessagesAtSend(savedMessagesData);
      setIsProcessing(true);
      resetProgress();
      streamingListResultsRef.current = [];
      setStreamingListResults([]);
      setProgressPhase("routing");
      setStreamingContent("");

      try {
        const response = await askConversation({
          business_report_id: businessReportId,
          snapshot_collection_id: snapshotCollectionId,
          subcollection_id: subcollectionId ?? null,
          conversation_id: conversationId,
          query,
          filters: activeFilters,
          contract_family_projection: contractFamilyProjection,
        }).unwrap();

        setConversationId(response.conversation_id);
        setQueryId(response.query_id);
      } catch {
        toast.error("Failed to send message. Please try again.");
        setIsProcessing(false);
        setLocalMessages(prev => prev.filter(m => m.id !== userMessage.id));
        setLastSentMessageId(null);
      }
    },
    [
      askConversation,
      businessReportId,
      snapshotCollectionId,
      subcollectionId,
      conversationId,
      activeFilters,
      contractFamilyProjection,
      lastSentMessageId,
      resetProgress,
      savedMessagesData,
      setProgressPhase,
    ],
  );

  const handleCancel = React.useCallback(async () => {
    if (!conversationId) return;
    try {
      await cancelConversation({ conversation_id: conversationId }).unwrap();
    } catch {
      toast.error("Failed to cancel. The task may still be running.");
    }
    setIsProcessing(false);
    setQueryId(null);
    setStreamingContent("");
    streamingListResultsRef.current = [];
    setStreamingListResults([]);
    routingModeRef.current = "PROSE";
    sourceRef.current = null;
    setSource(null);
    resetProgress();
  }, [cancelConversation, conversationId, resetProgress]);

  const handleEnd = React.useCallback(() => {
    setIsProcessing(false);
    setQueryId(null);
    setStreamingContent("");
    streamingContentRef.current = "";
    streamingListResultsRef.current = [];
    setStreamingListResults([]);
    routingModeRef.current = "PROSE";
    setProgressPhase("complete");
  }, [setProgressPhase]);

  const handleNewChat = React.useCallback(() => {
    setConversationId(null);
    setQueryId(null);
    setIsProcessing(false);
    setStreamingContent("");
    setLocalMessages([]);
    setLastSentMessageId(null);
    streamingListResultsRef.current = [];
    setStreamingListResults([]);
    routingModeRef.current = "PROSE";
    sourceRef.current = null;
    setSource(null);
    resetProgress();
    toggleShowHistory(false);
  }, [toggleShowHistory, resetProgress]);

  const handleSelectConversation = React.useCallback(
    (conversation: SnapshotConversation) => {
      setConversationId(conversation.id);
      setQueryId(null);
      setIsProcessing(false);
      setStreamingContent("");
      setLocalMessages([]);
      setLastSentMessageId(null);
      streamingListResultsRef.current = [];
      setStreamingListResults([]);
      sourceRef.current = null;
      setSource(null);
      resetProgress();
      toggleShowHistory(false);
    },
    [toggleShowHistory, resetProgress],
  );

  const handleRoutingResult = React.useCallback(
    (mode: string, _reasoning: string) => {
      routingModeRef.current = mode || "PROSE";
      if (mode === "LIST" || mode === "PROSE") {
        setProgressRoutingMode(mode);
        setProgressPhase("detecting_fields");
      }
    },
    [setProgressPhase, setProgressRoutingMode],
  );

  const handleSource = React.useCallback((s: string) => {
    sourceRef.current = s;
    setSource(s);
  }, []);

  const handleRewriting = React.useCallback(() => {
    setProgressPhase("rewriting");
  }, [setProgressPhase]);

  const handleContextSize = React.useCallback(
    (_tokens: number, _documents: number, mapReduce: boolean) => {
      setProgressMapReduce(mapReduce);
      if (mapReduce) setProgressPhase("reading_batches");
    },
    [setProgressMapReduce, setProgressPhase],
  );

  const handleScope = React.useCallback(
    (strategy: string, hyde: boolean) => {
      const known = ["EXHAUSTIVE", "CONCEPT", "LOOKUP", "STRUCTURED"].includes(strategy);
      setProgressScope(known ? (strategy as QaScopeStrategy) : null, hyde);
      setProgressPhase(strategy === "EXHAUSTIVE" ? "generating" : "retrieving");
    },
    [setProgressScope, setProgressPhase],
  );

  const handleRetrieving = React.useCallback(
    (_strategy: string, _candidates: number, selectedDocuments: number) => {
      setProgressSelectedDocuments(selectedDocuments);
      setProgressPhase("generating");
    },
    [setProgressSelectedDocuments, setProgressPhase],
  );

  const handleMapProgress = React.useCallback(
    (done: number, total: number) => {
      setProgressBatchProgress(done, total);
      if (total > 0 && done >= total) setProgressPhase("generating");
    },
    [setProgressBatchProgress, setProgressPhase],
  );

  const handleFieldsDetecting = React.useCallback(() => {
    setProgressPhase("detecting_fields");
  }, [setProgressPhase]);

  const handleFieldsDetected = React.useCallback(
    (fields: string[], _count: number, reused: boolean) => {
      detectedFieldsRef.current = fields;
      setProgressDetectedFields(fields, reused);
      if (routingModeRef.current === "LIST") {
        setProgressPhase("retrieving");
      } else {
        setProgressPhase("scoping");
      }
    },
    [setProgressDetectedFields, setProgressPhase],
  );

  const handleProseChunk = React.useCallback((chunk: string) => {
    setStreamingContent(prev => {
      const next = prev + chunk;
      streamingContentRef.current = next;
      return next;
    });
  }, []);

  const handleProseComplete = React.useCallback(
    (citationsJson: string) => {
      const content = streamingContentRef.current;
      if (!content && !citationsJson) return;
      let citations: Citation[] = [];
      try {
        citations = citationsJson ? JSON.parse(citationsJson) : [];
      } catch (e) {
        console.error("Failed to parse citations:", e);
        citations = [];
      }
      const isListMode = routingModeRef.current === "LIST";
      const assistantMessage: SnapshotConversationMessage = {
        id: ulid(),
        role: "assistant",
        content,
        mode: isListMode ? "LIST" : "PROSE",
        citations,
        matched_documents: null,
        source: sourceRef.current as "history" | "retrieval" | null,
        created_at: new Date().toISOString(),
      };
      setLocalMessages(prev => [...prev, assistantMessage]);
      setStreamingContent("");
      streamingContentRef.current = "";
      if (isListMode) {
        streamingListResultsRef.current = [];
        setStreamingListResults([]);
        setProgressPhase("complete");
      }
    },
    [setProgressPhase],
  );

  const handleFollowUp = React.useCallback((question: string) => {
    if (!question) return;
    const followUpMessage: SnapshotConversationMessage = {
      id: ulid(),
      role: "assistant",
      content: question,
      mode: "FOLLOW_UP",
      citations: null,
      matched_documents: null,
      source: null,
      created_at: new Date().toISOString(),
    };
    setLocalMessages(prev => [...prev, followUpMessage]);
  }, []);

  const handleListRetrieving = React.useCallback(() => {
    setProgressPhase("retrieving");
  }, [setProgressPhase]);

  const handleListDocuments = React.useCallback(
    (count: number) => {
      setProgressDocumentCount(count);
      setProgressPhase("analyzing");
    },
    [setProgressDocumentCount, setProgressPhase],
  );

  const handleListResults = React.useCallback(
    (resultsJson: string) => {
      try {
        const newResults: ListResult[] = JSON.parse(resultsJson);
        streamingListResultsRef.current = [...streamingListResultsRef.current, ...newResults];
        setStreamingListResults([...streamingListResultsRef.current]);
        addProgressAnalyzedCount(newResults.length);
        streamingContentRef.current = JSON.stringify(streamingListResultsRef.current);
      } catch (e) {
        console.error("Failed to parse list results:", e);
        streamingContentRef.current = resultsJson;
      }
    },
    [addProgressAnalyzedCount],
  );

  const handleError = React.useCallback(
    (message: string) => {
      toast.error(message || "An error occurred");
      setIsProcessing(false);
      setQueryId(null);
      setStreamingContent("");
      streamingContentRef.current = "";
      streamingListResultsRef.current = [];
      setStreamingListResults([]);
      routingModeRef.current = "PROSE";
      sourceRef.current = null;
      setSource(null);
      resetProgress();
    },
    [resetProgress],
  );

  React.useEffect(() => {
    if (!queryId) return;
    const timer = setTimeout(
      () => {
        handleError("Request timed out. Please try again.");
      },
      10 * 60 * 1000,
    );
    return () => clearTimeout(timer);
  }, [queryId, handleError]);

  const handleOutcomeClickWithFields = React.useCallback(
    (outcomeId: string) => {
      onOutcomeClick(outcomeId, detectedFieldsRef.current);
    },
    [onOutcomeClick],
  );

  const isProgressVisible = progressPhase !== "idle";
  const hasMessages = allMessages.length > 0 || isProcessing;

  if (showHistory) {
    return (
      <SnapshotQaHistory
        snapshotCollectionId={snapshotCollectionId}
        contractFamilyProjection={contractFamilyProjection}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        onBack={() => toggleShowHistory(false)}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between border-b p-4">
        <Button type="button" variant="ghost" size="xs" onClick={handleNewChat} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          New Chat
        </Button>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="xs" onClick={() => toggleShowHistory(true)} className="gap-1.5">
            <History className="h-3.5 w-3.5" />
            History
          </Button>
          {onToggleExpand && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onToggleExpand}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </Button>
          )}
          {onClose && (
            <Button type="button" variant="ghost" size="xs" onClick={onClose} title="Close">
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {!hasMessages && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <PathfinderTriangleIcon size={48} className="text-primary opacity-30" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Analyse the collection</p>
            <p className="text-xs text-muted-foreground">Ask questions about the snapshots in this collection</p>
          </div>
        </div>
      )}

      {hasMessages && (
        <SnapshotQaMessages
          messages={allMessages}
          lastSentMessageId={lastSentMessageId}
          streamingContent={isProgressVisible && routingModeRef.current === "LIST" ? "" : streamingContent}
          isProcessing={isProgressVisible && routingModeRef.current === "LIST" ? false : isProcessing}
          onCitationClick={onCitationClick}
          onOutcomeClick={handleOutcomeClickWithFields}
          onOpenQa={onOpenQa}
          progressMetrics={isProgressVisible ? progressMetrics : undefined}
          streamingListResults={streamingListResults.length > 0 ? streamingListResults : undefined}
          streamingSource={source}
        />
      )}

      <div className="p-4">
        <ChatInput
          onSendMessage={handleSubmit}
          isStreaming={isProcessing}
          isReady={true}
          placeholder="Ask any question"
          compact
          onCancel={handleCancel}
        />
      </div>

      {queryId && (
        <SnapshotQaWebsocket
          key={queryId}
          queryId={queryId}
          onRoutingResult={handleRoutingResult}
          onProseChunk={handleProseChunk}
          onProseComplete={handleProseComplete}
          onFollowUp={handleFollowUp}
          onError={handleError}
          onEnd={handleEnd}
          onListRetrieving={handleListRetrieving}
          onListDocuments={handleListDocuments}
          onListResults={handleListResults}
          onFieldsDetecting={handleFieldsDetecting}
          onFieldsDetected={handleFieldsDetected}
          onSource={handleSource}
          onRewriting={handleRewriting}
          onContextSize={handleContextSize}
          onMapProgress={handleMapProgress}
          onScope={handleScope}
          onRetrieving={handleRetrieving}
        />
      )}
    </div>
  );
};
